import { Form } from '@form/Form';
import FormCSVImport, { Field } from '@form/FormCSVImport';
import FormSubmit from '@form/FormSubmit';
import { Blockquote } from '@ui';
import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import useSWR from 'swr';
import { objectToFormData } from '../../../helpers/api';
import { useApi } from '../../../hooks/useApi';
import { pushAlert } from '../../../redux/actions/alertActions';

interface Props {
    formId: number;
    eventId?: number;
    cb?: () => void;
}

const BookingImportAssistant = ({ formId, eventId, cb }: Props) => {
    const { t } = useTranslation('bookings');
    const api = useApi();
    const dispatch = useDispatch();
    const [isLoading, setIsLoading] = useState(false);
    const { data } = useSWR(formId ? `form/${formId}` : null);

    const handleSubmit = useCallback(
        (data: any) => {
            setIsLoading(true);
            return api({
                url: `booking/${eventId}/import`,
                method: 'POST',
                data: objectToFormData({ formId, ...data, csv: data.csv[0] }),
                onSuccess: () => {
                    dispatch(pushAlert(t('booking.import.successMessage'), 'success'));
                    cb?.();
                },
                onBefore: () => setIsLoading(false)
            });
        },
        [eventId, t]
    );

    const fields = useMemo(
        (): Field[] => [
            { label: t('booking.import.fields.name'), name: 'user_name' },
            { label: t('booking.import.fields.email'), name: 'user_email' },
            ...(data?.data?.fields.filter((field) => !['wysiwyg'].includes(field.type)) || []).map((field) => ({
                ...field,
                autoMappingKeys: field.label?.replace(/\W+/g, '').split(' ')
            }))
        ],
        [data, t]
    );

    return (
        <>
            <Blockquote spacing>{t('booking.import.notice')}</Blockquote>
            <Form onSubmit={handleSubmit}>
                <FormCSVImport mapToFields={fields} />
                <FormSubmit isLoading={isLoading}>{t('booking.import.submit')}</FormSubmit>
            </Form>
        </>
    );
};

export default BookingImportAssistant;
